// Strukturierte Übungs-Variationen (inkl. Spieleranzahl-Anpassungen)

/**
 * @typedef {{ count: number | 'over4', text: string }} DrillVariationAdaptation
 * @typedef {{ content: string, adaptations: DrillVariationAdaptation[] }} DrillVariationModel
 */

/**
 * @param {unknown} item
 * @returns {DrillVariationAdaptation | null}
 */
function normalizeAdaptation(item) {
  if (!item || typeof item !== "object") return null;
  const countRaw = item.count ?? item.players;
  const text = String(item.text || "").trim();
  if (!text) return null;
  if (countRaw === "over4" || countRaw === "over_4" || countRaw === ">4") {
    return { count: "over4", text };
  }
  const count = Number(countRaw);
  if (!Number.isFinite(count) || count < 1) return null;
  if (count > 4) return { count: "over4", text };
  return { count: Math.floor(count), text };
}

/**
 * @param {unknown} value
 * @returns {DrillVariationAdaptation[]}
 */
export function normalizeVariationAdaptations(value) {
  if (!Array.isArray(value)) return [];
  return value.map(normalizeAdaptation).filter(Boolean);
}

/**
 * @param {unknown} item
 * @returns {DrillVariationModel}
 */
export function normalizeDrillVariation(item) {
  if (typeof item === "string") {
    return { content: String(item || "").trim(), adaptations: [] };
  }
  if (!item || typeof item !== "object") {
    return { content: "", adaptations: [] };
  }
  return {
    content: String(item.content || "").trim(),
    adaptations: normalizeVariationAdaptations(item.adaptations),
  };
}

/**
 * @param {unknown} drill
 * @param {(key: string) => string} [t]
 * @returns {DrillVariationModel[]}
 */
export function resolveDrillVariationModels(drill, t) {
  const fromRel = Array.isArray(drill?.variationItemsDetailed)
    ? drill.variationItemsDetailed
    : null;
  if (fromRel?.length) {
    return fromRel.map(normalizeDrillVariation).filter((v) => v.content);
  }
  const overrides = Array.isArray(drill?.variationOverrides)
    ? drill.variationOverrides
    : [];
  if (overrides.length > 0) {
    return overrides.map(normalizeDrillVariation).filter((v) => v.content);
  }
  const keys = Array.isArray(drill?.variationKeys) ? drill.variationKeys : [];
  if (typeof t === "function" && keys.length > 0) {
    return keys
      .map((key) => normalizeDrillVariation(t(key)))
      .filter((v) => v.content);
  }
  const items = Array.isArray(drill?.variationItems)
    ? drill.variationItems
    : [];
  return items.map(normalizeDrillVariation).filter((v) => v.content);
}

/**
 * Nur Basis-Texte (z. B. Stundenplanung).
 *
 * @param {unknown} drill
 * @param {(key: string) => string} t
 * @returns {string[]}
 */
export function resolveDrillVariationsFlat(drill, t) {
  return resolveDrillVariationModels(drill, t).map((v) => v.content);
}

/**
 * @param {(key: string, params?: Record<string, string>) => string} t
 * @param {DrillVariationModel} variation
 */
export function formatVariationAdaptationLabel(t, count) {
  if (count === "over4")
    return t("trainer.drills.playerCounts.adaptationOverFour");
  return t("trainer.drills.playerCounts.adaptationFor", {
    count: String(count),
  });
}

/**
 * @param {(key: string, params?: Record<string, string>) => string} t
 * @param {DrillVariationModel} variation
 */
export function variationModelToDisplay(variation, t) {
  const v = normalizeDrillVariation(variation);
  const adaptations = v.adaptations.map((a) => ({
    label: formatVariationAdaptationLabel(t, a.count),
    text: a.text,
  }));
  return { content: v.content, adaptations };
}

/**
 * @param {DrillVariationModel[]} variations
 */
export function variationModelsToSyncRows(variations) {
  return (variations || [])
    .map(normalizeDrillVariation)
    .filter((v) => v.content)
    .map((v, index) => ({
      sort_order: index + 1,
      content: v.content,
      adaptations: v.adaptations,
    }));
}
